/**
 * What's New — announcements shown inside each tenant's platform.
 *
 * Tabs across the approval buckets, then the matching announcements grouped into a collapsible
 * section per BU. Every item goes through approval by someone other than its creator.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTSelect } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JSX } from 'react';
import { deleteWhatsNew, getWhatsNew } from '../../Services/WhatsNew.service';
import type { WhatsNew } from '../../Types/WhatsNew.types';
import { WhatsNewApprovalStatus } from '../../Types/WhatsNew.types';
import {
  WHATS_NEW_APPROVE_OWN,
  WHATS_NEW_BUCKETS,
  WHATS_NEW_RESET_VIEWER,
  getWhatsNewActions,
} from '../../Utils/approvalRules';
import { groupWhatsNewByTenant } from '../../Utils/groupWhatsNewByTenant';
import type { ApprovalAction } from '../Modals/ApprovalModal';
import { ApprovalModal } from '../Modals/ApprovalModal';
import { DeleteWhatsNewModal } from '../Modals/DeleteWhatsNewModal';
import { WhatsNewViewersModal } from '../Modals/WhatsNewViewersModal';
import { WhatsNewCard } from '../WhatsNewCard/WhatsNewCard';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { useActionToast } from '@/Hooks/useActionToast';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { useTenants } from '@/Hooks/useTenants';
import { usePages } from '@/Pages/BUs/Pages/Hooks/usePagesQueries';
import { WhatsNewPreview, getTenantColor, getTenantIcon } from '@/Shared/brand';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { Tabs } from '@/Shared/components/Tabs/Tabs';
import { TruncatedListNotice } from '@/Shared/components/TruncatedListNotice/TruncatedListNotice';
import { MAX_LIST_LIMIT, isListTruncated } from '@/Shared/constants/listLimits';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const filterStyle = css`
  min-width: 200px;
`;

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const tabsBarStyle = css`
  padding: 0 var(--space-ml);
  border-bottom: 1px solid var(--color-border);
  overflow-x: auto;
`;

const groupsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  padding: var(--space-ml);
`;

const cardsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
  gap: var(--space-base);
`;

const emptyWrapStyle = css`
  padding: var(--space-l);
`;

const ALL_TENANTS = 'all';
const ALL_BUCKETS = 'all';

const WhatsNewList = (): JSX.Element => {
  const navigate = useNavigate();
  const { pages: privileges } = usePrivileges();
  const toast = useActionToast();
  const { profile } = useProfile();
  const { tenants, tenantOptions } = useTenants();
  const { data: pagesData } = usePages();

  /*
    Not a Flagsmith flag: `whats_new:approve_own` is a BFF permission, so it is read from the
    profile. It waives the four-eyes rule for its holder, and the BFF checks it again on approve —
    this only decides whether to offer the button or the "Outro aprovador" marker.
  */
  /*
    Two gates for each, on purpose: the flag says the panel offers the action, the BFF permission
    says the server allows it. Either one missing hides it — a flag alone would render a button
    that answers 403, and a permission alone would ignore the switch in Flagsmith.
  */
  const canApproveOwn =
    privileges.BUSINESS_UNITS.WHATS_NEW.APPROVE_OWN &&
    (profile?.permissions?.includes(WHATS_NEW_APPROVE_OWN) ?? false);
  const canResetViewer =
    privileges.BUSINESS_UNITS.WHATS_NEW.RESET_VIEWER &&
    (profile?.permissions?.includes(WHATS_NEW_RESET_VIEWER) ?? false);

  /*
    Which pages are their BU's main one. The announcement listing does not carry `isMain`, and the
    distinction matters on the card: choosing no page on create lands the announcement here.
  */
  const mainPageIds = useMemo(
    () => new Set((pagesData?.data ?? []).filter(page => page.isMain).map(page => page.id)),
    [pagesData],
  );

  const [tenantFilter, setTenantFilter] = useState<string>(ALL_TENANTS);
  const [bucket, setBucket] = useState<string>(ALL_BUCKETS);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<WhatsNew | null>(null);
  const [approval, setApproval] = useState<{ action: ApprovalAction; item: WhatsNew } | null>(null);
  const [deleting, setDeleting] = useState<WhatsNew | null>(null);
  const [viewersOf, setViewersOf] = useState<WhatsNew | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['whats-new', 'list'],
    queryFn: () => getWhatsNew({ limit: MAX_LIST_LIMIT }),
  });

  const allItems = useMemo(() => data?.data ?? [], [data]);

  const removeItem = useMutation({
    mutationFn: (item: WhatsNew) => deleteWhatsNew(item.id),
    onSuccess: () => {
      void refetch();
      toast.success('Anúncio excluído');
      setDeleting(null);
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível excluir o anúncio', error);
    },
  });

  const bucketCounts = useMemo(() => {
    const counts: Record<string, number> = { [ALL_BUCKETS]: allItems.length };
    for (const status of Object.values(WhatsNewApprovalStatus)) {
      counts[status] = allItems.filter(item => item.approvalStatus === status).length;
    }
    return counts;
  }, [allItems]);

  const groups = useMemo(() => {
    const scoped = allItems
      .filter(item => tenantFilter === ALL_TENANTS || item.tenantSlug === tenantFilter)
      .filter(item => bucket === ALL_BUCKETS || item.approvalStatus === bucket);

    return groupWhatsNewByTenant(scoped);
  }, [allItems, tenantFilter, bucket]);

  const filterOptions = useMemo(
    () => [
      { id: ALL_TENANTS, text: 'Todos os tenants', value: ALL_TENANTS },
      ...tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    ],
    [tenantOptions],
  );

  const handleCopy = (item: WhatsNew) => {
    const payload = JSON.stringify(
      {
        tenantSlug: item.tenantSlug,
        pageSlug: item.page?.slug ?? null,
        version: item.platformVersion?.version,
        welcomeMessage: item.welcomeMessage,
        subtitle: item.subtitle,
        items: item.items,
      },
      null,
      2,
    );

    void navigator.clipboard?.writeText(payload).catch(() => undefined);
    setCopiedId(item.id);
    setTimeout(() => {
      setCopiedId(current => (current === item.id ? null : current));
    }, 2000);
  };

  if (isLoading) {
    return (
      <MainContent id='whats-new-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='whats-new-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Anúncios exibidos dentro da plataforma de cada tenant. Todo item passa por aprovação de
            uma pessoa diferente de quem criou.
          </p>
          <div className={spacerStyle} />
          <div className={filterStyle}>
            <UTSelect
              options={filterOptions}
              canSelectMultiple={false}
              hasCheckbox={false}
              defaultSelectedOptions={[filterOptions[0]]}
              inputOptions={{ textFieldProps: { placeholder: 'Todos os tenants' } }}
              onSelectedOptionsChange={options => {
                setTenantFilter(options.length > 0 ? String(options[0].value) : ALL_TENANTS);
              }}
            />
          </div>
          {privileges.BUSINESS_UNITS.WHATS_NEW.CREATE ? (
            <UTButton
              fitContent
              id='whats-new-create'
              text="Criar What's New"
              icon='Arrows.Plus'
              onClick={() => {
                navigate('/business-units/whats-new/create');
              }}
            />
          ) : null}
        </div>

        {isListTruncated(data?.pagination.total, allItems.length) ? (
          <TruncatedListNotice
            id='whats-new-truncated'
            shown={allItems.length}
            total={data?.pagination.total ?? allItems.length}
            noun='anúncios'
          />
        ) : null}

        <section className={panelStyle}>
          <div className={tabsBarStyle}>
            <Tabs
              items={WHATS_NEW_BUCKETS.map(tab => ({
                id: tab.id,
                label: tab.label,
                badge: bucketCounts[tab.id] ?? 0,
              }))}
              activeId={bucket}
              onChange={setBucket}
            />
          </div>

          {groups.length === 0 ? (
            <div className={emptyWrapStyle}>
              <EmptyState
                id='whats-new-empty'
                icon='Communication.CheckCircle'
                title='Nada neste status'
                description='Quando alguém enviar um anúncio para aprovação, ele aparece aqui.'
              />
            </div>
          ) : (
            <div className={groupsStyle}>
              {groups.map(group => {
                const tenant = tenants.find(entry => entry.slug === group.tenantSlug);

                return (
                  <CollapsibleSection
                    key={group.tenantSlug}
                    id={`whats-new-section-${group.tenantSlug}`}
                    title={group.tenantSlug}
                    monoTitle
                    icon={getTenantIcon({ slug: group.tenantSlug, iconName: tenant?.iconName })}
                    iconColor={getTenantColor(group.tenantSlug)}
                    count={group.countLabel}
                    summary={group.pendingLabel}
                    open={!collapsed[group.tenantSlug]}
                    onToggle={() => {
                      setCollapsed(current => ({
                        ...current,
                        [group.tenantSlug]: !current[group.tenantSlug],
                      }));
                    }}
                  >
                    <div className={cardsGridStyle}>
                      {group.items.map(item => (
                        <WhatsNewCard
                          key={item.id}
                          item={item}
                          tenantVersion={tenant?.version}
                          isMainPage={item.page ? mainPageIds.has(item.page.id) : false}
                          isCopied={copiedId === item.id}
                          actions={getWhatsNewActions(item, profile?.id, {
                            submit: privileges.BUSINESS_UNITS.WHATS_NEW.SUBMIT,
                            approve: privileges.BUSINESS_UNITS.WHATS_NEW.APPROVE,
                            edit: privileges.BUSINESS_UNITS.WHATS_NEW.EDIT,
                            delete: privileges.BUSINESS_UNITS.WHATS_NEW.DELETE,
                            approveOwn: canApproveOwn,
                          })}
                          onCopy={handleCopy}
                          onView={setPreviewItem}
                          onEdit={target => {
                            navigate(`/business-units/whats-new/${target.id}/edit`);
                          }}
                          onSubmit={target => {
                            setApproval({ action: 'submit', item: target });
                          }}
                          onApprove={target => {
                            setApproval({ action: 'approve', item: target });
                          }}
                          onReject={target => {
                            setApproval({ action: 'reject', item: target });
                          }}
                          onDelete={setDeleting}
                          /*
                            Seeing who read it is not the same authority as undoing it: the BFF
                            gates the list with `whats_new:read`/`manage`, the same as this screen,
                            and only the reset with `whats_new:reset_viewer`. So anyone who is
                            here can open the list; the button inside it is what checks further.
                          */
                          onViewers={item.viewerCount === undefined ? undefined : setViewersOf}
                        />
                      ))}
                    </div>
                  </CollapsibleSection>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {approval ? (
        <ApprovalModal
          open
          action={approval.action}
          whatsNewId={approval.item.id}
          onClose={() => {
            setApproval(null);
          }}
          onSuccess={() => {
            void refetch();
          }}
        />
      ) : null}

      {viewersOf ? (
        <WhatsNewViewersModal
          open
          item={viewersOf}
          canReset={canResetViewer}
          onClose={() => {
            setViewersOf(null);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteWhatsNewModal
          open
          item={deleting}
          isDeleting={removeItem.isPending}
          error={removeItem.error?.message ?? null}
          onClose={() => {
            removeItem.reset();
            setDeleting(null);
          }}
          onConfirm={target => {
            removeItem.mutate(target);
          }}
        />
      ) : null}

      {previewItem ? (
        <WhatsNewPreview
          tenantSlug={previewItem.tenantSlug}
          open
          title={previewItem.welcomeMessage}
          subtitle={previewItem.subtitle ?? undefined}
          footerMessage={previewItem.footer ?? undefined}
          items={previewItem.items.map(entry => ({
            id: entry.id,
            icon: entry.icon as never,
            title: entry.title,
            body: entry.body,
            color: entry.color,
            order: entry.order,
          }))}
          actionButton={{
            text: previewItem.confirmButtonText ?? 'Entendi',
            onClick: () => {
              setPreviewItem(null);
            },
          }}
          onClose={() => {
            setPreviewItem(null);
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { WhatsNewList };
